import { Vector2 } from "three";
/**
 * Given a {@link TilingScheme} and a {@link Rectangle}, this class will iterate over all intersecting tiles at the
 * requested zoom level. This iterator yields a {@link Vec2}. The zoom level is implicit as it was provided in the
 * constructor.
 *
 * ** Warning **
 *
 * This iterator will always yield the same {@link Vec2} object, but with different values. Do not cache the object as
 * it will change between iterations.
 */
export class TileRegionIterator {
    constructor(tilingScheme, rectangle, zoomLevel) {
        /**
         * The tile that the minimum point of the input rectangle is inside. This may be equal to _maxTile
         * @private
         */
        this._minTile = new Vector2();
        /**
         * The tile that the maximum point of the input rectangle is inside. This may be equal to _minTile
         * @private
         */
        this._maxTile = new Vector2();
        /**
         * A single allocated 'Vec2'. This object is what gets yielded by the iterator on every 'next' call. This reduces
         * the number of allocations we need to make. We only yield the object as read-only.
         * @private
         */
        this._tile = { x: 0, y: 0 };
        /**
         * The internal state of the iterator which is used for tracking iterations. We can't use _tile for this due to the
         * implementation.
         * @private
         */
        this._state = { x: 0, y: 0 };
        const minTile = tilingScheme.positionToTileXY(rectangle.southwest, zoomLevel);
        const maxTile = tilingScheme.positionToTileXY(rectangle.northeast, zoomLevel);
        // Some tiling schemes may use different origins. While rectangle.southwest will be <= rectangle.northeast, the
        // resulting tile regions may not retain this property. As such we need to select the min and max from 'minTile'
        // and 'maxTile' explicitly instead of relying on them to implicitly be correct (they won't be)
        this._minTile.x = Math.min(minTile.x, maxTile.x);
        this._minTile.y = Math.min(minTile.y, maxTile.y);
        this._maxTile.x = Math.max(minTile.x, maxTile.x);
        this._maxTile.y = Math.max(minTile.y, maxTile.y);
        // Initialize our state
        this.reset();
    }
    /**
     * Reset the iterator to its default state. Use this to allow reusing the iterator for another iteration cycle.
     */
    reset() {
        this._state.x = this._minTile.x;
        this._state.y = this._minTile.y;
    }
    next(...args) {
        // If we're outside the tile region then the iterator is done
        if (this._state.y > this._maxTile.y) {
            return {
                done: true,
                value: undefined,
            };
        }
        // Capture the current state in _tile to yield
        this._tile.x = this._state.x;
        this._tile.y = this._state.y;
        // Advance the iterator. This will be the value we try and yield in the next iteration.
        this._state.x += 1;
        // Move to the start of the next row if we've reached the end of the current row
        if (this._state.x > this._maxTile.x) {
            this._state.x = this._minTile.x;
            this._state.y += 1;
        }
        return {
            done: false,
            value: this._tile,
        };
    }
    [Symbol.iterator]() {
        return this;
    }
}
